import { Outlet } from 'react-router-dom';
import { UserNav } from '../components/UserNav';
import AppSidebar from '../components/AppSidebar';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Users,
  Building2,
  Calendar,
  ListChecks,
  ClipboardList,
  CheckSquare,
  CreditCard,
  FileText,
  Zap,
  TrendingUp,
  Receipt,
  UserCheck,
  Calculator,
  Search,
  PenTool,
  BarChart3
} from 'lucide-react';

export default function DashboardLayout() {
  const { isApprover, profile } = useAuth();

  // Check if user can access leads (Sales, Founder, or Marketing Intern function)
  const canAccessLeads = profile?.role === 'CupShup' &&
    (profile?.function === 'Sales' || profile?.function === 'Founder' || profile?.function === 'Marketing Intern');

  // Check if user can access P & L (Founder function only)
  const canAccessPL = profile?.role === 'CupShup' && profile?.function === 'Founder';

  // Check if user can access Automations (Sales or Founder function)
  const canAccessAutomations = profile?.role === 'CupShup' &&
    (profile?.function === 'Sales' || profile?.function === 'Founder');

  // Check if user can access SEO tools (Sales or Founder function)
  const canAccessSEO = profile?.role === 'CupShup' &&
    (profile?.function === 'Sales' || profile?.function === 'Founder');

  // Check if user can access Invoicing (Sales, Founder, and Finance functions)
  const canAccessInvoicing = profile?.role === 'CupShup' && (profile?.function === 'Sales' || profile?.function === 'Founder' || profile?.function === 'Finance');

  // Check if user can access Operations (Operation/Operations and Founder functions)
  const canAccessOperations = profile?.role === 'CupShup' && (
    profile?.function === 'Operations' ||
    profile?.function === 'Operation' ||
    profile?.function === 'Founder'
  );

  // Check if user can access Clients (exclude Operation/Operations functions)
  const canAccessClients = profile?.role === 'CupShup' &&
    profile?.function !== 'Operation' &&
    profile?.function !== 'Operations';

  // Define base navigation items grouped by section
  const groupedNavigation = [
    {
      title: 'Main',
      items: [
        {
          name: 'Dashboard',
          href: '/cupshup',
          icon: LayoutDashboard
        }
      ]
    },
    {
      title: 'Resources',
      items: [
        {
          name: 'Vendors',
          href: '/cupshup/vendors',
          icon: Users
        },
        ...(canAccessClients ? [{
          name: 'Clients',
          href: '/cupshup/clients',
          icon: Building2
        }] : [])
      ]
    },
    {
      title: 'Operations',
      items: [
        {
          name: 'Activities',
          href: '/cupshup/activities',
          icon: Calendar
        },
        ...(canAccessOperations ? [{
          name: 'Operations',
          href: '/cupshup/operations',
          icon: Calculator
        }] : []),
        {
          name: 'Mapped Activities',
          href: '/cupshup/mapped-activities',
          icon: ListChecks
        },
        {
          name: 'Mapped Tasks',
          href: '/cupshup/mapped-tasks',
          icon: ClipboardList
        },
        {
          name: 'Todo',
          href: '/cupshup/todo',
          icon: CheckSquare
        }
      ]
    },
    {
      title: 'Intelligence',
      items: [
        ...(canAccessAutomations ? [{
          name: 'Automations',
          href: '/cupshup/automations',
          icon: Zap
        }] : []),
        ...(canAccessSEO ? [{
          name: 'SEO',
          icon: Search,
          children: [
            {
              name: 'SEO Tools',
              href: '/cupshup/seo',
              icon: Search
            },
            {
              name: 'Blog Writing',
              href: '/cupshup/seo/blog-writing',
              icon: PenTool
            },
            {
              name: 'Keyword Research',
              href: '/cupshup/seo/keyword-research',
              icon: Search
            },
            {
              name: 'Site Audit',
              href: '/cupshup/seo/site-audit',
              icon: BarChart3
            }
          ]
        }] : [])
      ]
    },
    {
      title: 'Management & Finance',
      items: [
        ...(isApprover ? [{
          name: 'Review Requests',
          href: '/cupshup/submitted-requests',
          icon: CheckSquare
        }] : []),
        ...(canAccessInvoicing ? [{
          name: 'Invoicing',
          href: '/cupshup/invoicing',
          icon: Receipt
        }] : []),
        ...(canAccessPL ? [{
          name: 'P & L',
          href: '/cupshup/pl',
          icon: TrendingUp
        }] : []),
        {
          name: 'My Payment Requests',
          href: '/cupshup/my-payment-requests',
          icon: CreditCard
        }
      ]
    },
    {
      title: 'HR & Marketing',
      items: [
        ...(canAccessLeads ? [{
          name: 'Leads',
          href: '/cupshup/leads',
          icon: FileText
        }] : []),
        {
          name: 'HRMS',
          href: '/cupshup/hrms',
          icon: UserCheck
        }
      ]
    }
  ];

  // Filter out empty groups (if any)
  const cupShupNavigation = groupedNavigation.filter(group => group.items.length > 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <AppSidebar navigation={cupShupNavigation} />

      {/* Main content area - Remove pt-20 */}
      <div className="flex flex-1 flex-col lg:pl-72">
        {/* Desktop header - hidden on mobile */}
        <div className="hidden lg:sticky lg:top-0 lg:z-40 lg:flex lg:h-16 lg:shrink-0 lg:items-center lg:justify-end lg:gap-x-4 lg:border-b lg:border-gray-200 lg:bg-white lg:px-6 lg:shadow-sm">
          <UserNav />
        </div>

        {/* Main content - Add mobile padding, reset on large screens */}
        <main className="flex-1 pt-16 lg:pt-0">
          <div className="py-4 sm:py-6 px-4 sm:px-6 lg:px-8 max-w-full">
            <Outlet />
          </div>
        </main>
      </div>

    </div>
  );
}